import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import {captureFatalException, captureIgnoredException} from '../../services/logging'

import {
  Carrier,
  CarrierId,
  Color,
  ColorId,
  Country,
  CountryCode,
  FinishGroup,
  FinishGroupId,
  Material,
  MaterialConfig,
  MaterialConfigId,
  MaterialGroup,
  MaterialGroupId,
  MaterialId,
  PrintingMethod,
  PrintingMethodId,
  Provider,
} from '../../types'

import {signOut} from './auth'
import {openFatalErrorModal} from './modal'

export interface CoreState {
  materialConfigs: Record<MaterialConfigId, MaterialConfig>
  colors: Record<ColorId, Color>
  materials: Record<MaterialId, Material>
  materialGroups: Record<MaterialGroupId, MaterialGroup>
  finishGroups: Record<FinishGroupId, FinishGroup>
  provider: Provider | null
  countries: Record<CountryCode, Country>
  printingMethods: Record<PrintingMethodId, PrintingMethod>
  carriers: Record<CarrierId, Carrier>
}

const initialState: CoreState = {
  materialConfigs: {},
  colors: {},
  materials: {},
  materialGroups: {},
  finishGroups: {},
  provider: null,
  countries: {},
  printingMethods: {},
  carriers: {},
}

export const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    initialDataReceived: (state, action: PayloadAction<Partial<CoreState>>) => {
      return {
        ...state,
        ...action.payload,
      }
    },
    setProvider: (state, action: PayloadAction<{provider: Provider | null}>) => {
      state.provider = action.payload.provider
    },
  },
  extraReducers: builder => {
    builder.addCase(signOut.pending, state => {
      state.provider = null
    })
  },
})

export const {initialDataReceived, setProvider} = coreSlice.actions

type FatalErrorActionPayload = {
  error: Error
}

export const fatalError = createAsyncThunk(
  'core/fatalError',
  async (payload: FatalErrorActionPayload, {dispatch}) => {
    // Ignore promise cancelled errors (e.g. when price request got cancelled)
    if (payload.error.name === 'PromiseCancelledError') {
      return
    }

    // Handle feathers authentication error
    if (payload.error.name === 'NotAuthenticated') {
      captureIgnoredException(payload.error)
      dispatch(signOut())
      return
    }

    // Let's track all cases which lead to a fatalError
    captureFatalException(payload.error)
    dispatch(openFatalErrorModal({error: payload.error}))
  }
)

export default coreSlice.reducer
